import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";

import API from "Payments/PaymentPage/API";
import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";
import { usePollForPaymentStatus } from "Payments/PaymentPage/Shared/usePollForPaymentStatus";

import FnrButton, { ButtonIcon, ButtonText } from "UI/FnrButton3";

FinalizePaymentButton.propTypes = {
  isLoading: PropTypes.oneOf([null, "cancel", "finalize"]),
  setIsLoading: PropTypes.func.isRequired,
  setErrorMsg: PropTypes.func.isRequired,
};

export default function FinalizePaymentButton({
  isLoading,
  setIsLoading,
  setErrorMsg,
}) {
  const { state, dispatch } = usePaymentPage();
  const { fundColorSafe } = state.fund;
  const [shouldSubmit, setShouldSubmit] = useState(false);

  const clearLoading = useCallback(() => {
    setIsLoading(null);
  }, [setIsLoading]);

  usePollForPaymentStatus({
    forScreen: "modifyOrCancel",
    setIsSubmitting: clearLoading,
    setSubmitError: setErrorMsg,
  });

  const handleButtonClick = () => {
    if (isLoading) return;
    setShouldSubmit(true);
  };

  useEffect(() => {
    if (!shouldSubmit) return;

    const amount = state.modifyPayment?.originalAmount ?? 0;

    const submitPaymentCall = API.submitPayment({
      fundID: state.fund.ID,
      amount: amount,
      paymentMethodID: state.selectedPaymentMethodID,
      paymentID: state.modifyPayment?.paymentID,
      applyToExistingPledge: true,
    });

    setIsLoading("finalize");
    submitPaymentCall
      .post()
      .then((res) => {
        dispatch({
          type: "setSubmitPaymentResponse",
          payload: res,
        });
        dispatch({
          type: "setAmount",
          payload: Number(amount / 100).toFixed(2),
        });
        // NOTE: the usePollForPaymentStatus hook takes over at this point.
      })
      .catch((err) => {
        setIsLoading(null);
        if (err.error === "already_processed") {
          dispatch({ type: "setModifyPaymentAlreadyCompleted" });
        } else if (err.error === "payments_killswitch_enabled") {
          setErrorMsg(err.message);
        } else if (err.error === "fund_received_payment_too_recently") {
          setErrorMsg(
            "You've already made a payment to this fund in the past " +
              err.lookbackWindowInMinutes +
              " minutes. Please wait to try again."
          );
        } else {
          setErrorMsg(
            "There was a problem finalizing your payment. Please DO NOT try again. Contact help@flocknote.com about the status of your payment."
          );
        }
      })
      .finally(() => {
        setShouldSubmit(false);
      });
    return () => {
      if (submitPaymentCall.cancel) submitPaymentCall.cancel();
    };
  }, [
    shouldSubmit,
    dispatch,
    state.selectedPaymentMethodID,
    state.modifyPayment?.paymentID,
    state.modifyPayment?.originalAmount,
    state.fund.ID,
    setErrorMsg,
    setIsLoading,
  ]);

  return (
    <FnrButton
      color={fundColorSafe}
      size="large"
      fullWidth
      style={{ marginTop: 28 }}
      loading={isLoading === "finalize"}
      disabled={isLoading === "cancel"}
      onClick={handleButtonClick}
    >
      <ButtonText>{`Looks Good`}</ButtonText>
      <ButtonIcon fas fa-thumbs-up />
    </FnrButton>
  );
}
